import EChart from "./EChart";
import {Alert, Box, Grid, TextField, useTheme} from "@mui/material/";
import StatisticsFilterPanel from "../StatisticsFilterPanel";
import {msg, Trans} from '@lingui/macro';
import {useLocation, useNavigate} from "react-router-dom";
import useLogout from "../../../hooks/useLogout";
import {useLingui} from "@lingui/react";
import {useEffect, useMemo, useRef, useState} from 'react';
import {cyan, green, grey, orange, pink, red, yellow} from '@mui/material/colors';
import {blueberryTwilightPalette} from '@mui/x-charts/colorPalettes';
import httpReq from "../../../ce-services/http-req";
import * as echarts from "echarts";
import {Button, MenuItem, Snackbar} from "@mui/material";

export const chartPalette = [
    "#28BDED", "#FAD452", "#E0266C", "#B44860", "#425B61",
    "#3C3C3B", "#C9D3F3", "#2A95B8", "#1A5C74", "#E38DAA",
    "#EFDF67", "#8BA6E5", "#5070B6", "#DBDBDB", "#163E84",
    "#C1E5F5", "#017585", "#D1AC0D", "#747474", "#75BCC2",
    "#0E2841"
];

const Dashboard = ({isMobile}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const logout = useLogout();
    const {_} = useLingui();
    const state = location.state;
    const token = state?.token || null;
    const init = useMemo(() => state?.init || {}, [state?.init]);

    const [user, setUser] = useState({});
    const [formModel, setFormModel] = useState({});
    const [formList, setFormList] = useState([]);
    const [charts, setCharts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [areasGeoJson, setAreasGeoJson] = useState(null)
    const [colSize, setColSize] = useState(2);
    const [errorMessage, setErrorMessage] = useState(null);

    const theme = useTheme();
    const drawerWidth = theme.mixins.drawerWidth;
    const chartRefs = useRef([]);

    const init_charts = useMemo(() => ([
        {
            apiNum: 1,
            title: {
                text: _(msg`Crash severity`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'pie',
            payload: {
                chartField: {reportType: 0, target: 'crash', fieldName: 'Crash severity'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            xLabel: _(msg`Crash severity`),
            yLabel: _(msg`Number of crashes`),
            colorMap: {
                type: 'ordinal',
                colors: chartPalette.slice(0, 6)
            }
        },
        {
            apiNum: 2,
            title: {
                text: _(msg`Crashes by year`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'bar',
            payload: {
                chartField: {reportType: 0, target: 'crash', fieldName: 'Crash date'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            xLabel: _(msg`Year`),
            yLabel: _(msg`Number of crashes`),
            colorMap: {
                type: 'ordinal',
                colors: chartPalette.slice(6, 12)
            }
        },
        {
            apiNum: 3,
            title: {
                text: _(msg`Crashes by gender`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'bar',
            payload: {
                chartField: {reportType: 0, target: 'people', fieldName: 'Gender'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            xLabel: _(msg`Victim's gender`),
            yLabel: _(msg`Number of crashes`),
            colorMap: {
                type: 'ordinal',
                colors: [chartPalette[2], chartPalette[9], chartPalette[1]]
            }
        },
        {
            apiNum: 4,
            title: {
                text: _(msg`Crashes by area type`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'treemap',
            payload: {
                chartField: {reportType: 0, target: 'road', fieldName: 'Area type'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            xLabel: _(msg`Crashes by area type`),
            yLabel: _(msg`Number of crashes`),
            colorMap: {
                type: 'ordinal',
                colors: chartPalette
            }
        },
        {
            apiNum: 5,
            title: {
                text: _(msg`Crashes by region`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'map',
            payload: {
                chartField: {reportType: 0, target: 'crash', fieldName: 'State'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            mapType: 'areas',
            xLabel: '',
            yLabel: '',
            colorMap: {
                type: 'visualMap',
                visualMin: 0,
                visualMax: 100,
                inRange: {
                    color: [chartPalette[6], chartPalette[4]]
                }
            }
        },
        {
            apiNum: 6,
            title: {
                text: _(msg`Crashes by road user type`),
                show: false,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    color: '#fff'
                }
            },
            type: 'pie',
            payload: {
                chartField: {reportType: 0, target: 'people', fieldName: 'Road user type'},
                fields: init.configuration,
                filters: []
            },
            data: [],
            isLoading: false,
            xLabel: _(msg`Crashes by road user type`),
            yLabel: _(msg`Number of crashes`),
            colorMap: {
                type: 'ordinal',
                colors: chartPalette.slice(3, 9)
            }
        },
    ]), [init.configuration]);

    useEffect(() => {
        const hasMapChart = init_charts.some(chart => chart.type === 'map');
        if (!hasMapChart) return;

        fetch('/maps/areas.geojson')
            .then(res => res.json())
            .then(geoJson => {
                echarts.registerMap('areas', geoJson);
                setAreasGeoJson(geoJson)
                setMapReady(true);
            })
            .catch(err => console.error('Errore nel caricamento del geojson areas ', err));
    }, [init_charts]);

    useEffect(() => {
        if (!token) return logout();
        if (isMobile) return navigate('/home', {state: {token, init}});
    }, [token, logout, isMobile, navigate, init]);

    useEffect(() => {
        const {configuration, user} = init;
        setUser(user);
        setFormModel(configuration);

        const sortedForms = Object.keys(configuration)
            .sort((a, b) => configuration[a].position - configuration[b].position);
        setFormList(sortedForms);
        console.log('configuration', configuration);
        setCharts(init_charts);
    }, [init, init_charts]);

    useEffect(() => {
        const containsMap = charts.some(c => c.type === 'map');
        if (!formModel || dataLoaded || charts.length === 0) return;
        if (containsMap && !mapReady) return;

        const fetchAllCharts = async () => {
            const updatedCharts = await Promise.all(
                charts.map(async (chart) => {
                    const data = await fetchChartData(chart);
                    return {
                        ...chart,
                        isLoading: false,
                        data: data || []
                    };
                })
            );
            setCharts(updatedCharts);
            setFilters([]);
            setDataLoaded(true);
        };

        fetchAllCharts();
    }, [formModel, charts, dataLoaded, filters, mapReady]);

    const fetchChartData = async (chart) => {
        const payload = chart.payload;
        payload.filters = filters.length > 0 ? filters : [];

        const res = await httpReq('/AdamsStats/GetChartData', 'POST', payload, true);
        if (res.responseAnyError) return null;

        let resData = res.responseData;
        const target = payload.chartField.target;
        const fieldName = payload.chartField.fieldName;

        const foundGroup = formModel[target];
        if (!foundGroup) return null;

        const foundField = foundGroup.elements.find(f => f.label === fieldName);
        if (!foundField) return null;

        const foundOptions = foundField.options;

        if (foundOptions.length > 0) {
            resData = foundOptions.map(opt => {
                const match = res.responseData.find(r =>
                    r.label === opt.label || r.label === opt.value.toString()
                );
                const value = match ? match.value : 0;

                if (chart.type === 'map') {
                    return {label: opt.label, name: opt.label, value};
                }
                return {label: opt.label, value};
            });
        }

        const mappedResData = resData.map(opt => {
            if (opt.otherFormats) {
                return {...opt, label: opt.otherFormats[2].value};
            }
            return opt;
        });

        const result = [];
        mappedResData.reduce((acc, val) => {
            const key = chart.type === 'map' ? val.name : val.label;
            if (!acc[key]) {
                acc[key] = chart.type === 'map'
                    ? {name: key, value: 0}
                    : {label: key, value: 0};
                result.push(acc[key]);
            }
            acc[key].value += val.value;
            return acc;
        }, {});

        return result;
    };

    const handleCharts = (event, index) => {
        setCharts(prevCharts =>
            prevCharts.map(chart =>
                chart.apiNum === index + 1
                    ? {...chart, type: event.target.value}
                    : chart
            )
        );
    };

    const exportChartsAsFiles = async () => {
        const files = [];

        for (let index = 0; index < charts.length; index++) {
            const chart = charts[index];
            const imgData = chartRefs.current[index]?.getImage();

            if (!imgData) continue;

            const base64Content = imgData.replace(/^data:image\/png;base64,/, "");

            const setFileName = (title = "") => {
                const t = title.toLowerCase();

                if (t.includes("by year")) return "crashes_by_year";
                if (t.includes("by gender")) return "crashes_by_gender";
                if (t.includes("by area type")) return "crashes_by_area_type";
                if (t.includes("severity")) return "crashes_by_severity";
                if (t.includes("by region")) return "crashes_by_administrative_division";
                if (t.includes("by road user type")) return "crashes_by_user_type";

                return "img_chart";
            };

            files.push({
                name: setFileName(chart.title?.text),
                fileContent: base64Content
            });
        }

        return files;
    };


    const downloadDashboard = async () => {
        /*
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        let yPos = 10;

        const addChartsToPdf = async () => {
            for (let index = 0; index < charts.length; index++) {
                const imgData = chartRefs.current[index]?.getImage();
                if (!imgData) continue;

                await new Promise((resolve) => {
                    const img = new Image();
                    img.src = imgData;
                    img.onload = () => {
                        const imgWidth = img.width;
                        const imgHeight = img.height;
                        const ratio = Math.min( (pageWidth - 20) / imgWidth, 80 / imgHeight );

                        const renderWidth = imgWidth * ratio;
                        const renderHeight = imgHeight * ratio;

                        const x = (pageWidth - renderWidth) / 2;
                        const y = yPos;

                        pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);

                        yPos += renderHeight + 10;

                        if (yPos > pageHeight - 30 && index < charts.length - 1) {
                            pdf.addPage();
                            yPos = 10;
                        }

                        resolve();
                    };
                });
            }

            pdf.save("dashboard.pdf");
        };

        addChartsToPdf();
        */
        const filesArray = await exportChartsAsFiles();
        console.log("Array dei file:", filesArray)

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        const body = {
            images: filesArray,
            fromDate: formatDate(new Date()),
            toDate: formatDate(new Date()),
            templateName: 'ADaMSPeriodicCrashReport'
        };

        const res = await httpReq('ReportDoc/Generate', 'POST', body, true);
        console.log('res downloadDashboard ', res);
        if (!res.responseAnyError) {
            downloadWordFile(res.responseData.fileContent, "ADaMSPeriodicCrashReport.docx");
        } else {
            setErrorMessage(res.responseMessage)
        }


    };

    const downloadWordFile = (base64Data, fileName) => {
        // ripulisce spazi, newline, ecc.
        const cleaned = base64Data.replace(/\s/g, "");

        // decodifica base64 → binary
        const byteCharacters = atob(cleaned);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        // crea un Blob Word (docx è un zip)
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        });

        // crea link temporaneo per scaricare
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box
            ml={`${drawerWidth}px`}
            pl={2}
            pr={2}
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            sx={{
                '& .overload-theme--header': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText
                }
            }}
        >
            {/*
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: 1.5
            }}>
                <Typography sx={{fontSize: 18}} variant="h3">
                    <Trans>Dashboard</Trans>
                </Typography>
                <Box sx={{alignSelf: 'end'}}>
                    <UserControls noElev={true} state={state}/>
                </Box>
            </Box>

            <Divider flexItem/>
            */}

            <Snackbar
                open={Boolean(errorMessage)}
                autoHideDuration={5000} // si chiude dopo 5 secondi
                onClose={() => setErrorMessage(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity="error"
                    onClose={() => setErrorMessage(null)}
                    sx={{ width: "100%" }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>

            {formList.length > 0 && (
                <StatisticsFilterPanel
                    title={"Dashboard"}
                    newLabel={"Dashboard"}
                    formList={formList}
                    filterDisabled={false}
                    onFilterSubmit={() => {
                    }}
                    onFilterReset={() => {
                    }}
                    setDataLoaded={setDataLoaded}
                    user={user}
                    formModel={formModel}
                    isMobile={isMobile}
                    setFilters={setFilters}
                />
            )}

            <Box sx={{mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                <Button
                    sx={{
                        background: theme.palette.newColors.cyanGradient,
                    }}
                    variant='contained'
                    size='small'
                    onClick={() => {
                        downloadDashboard()
                    }}
                >
                    <Trans>Download report</Trans>
                </Button>
                <TextField size={'small'} sx={{minWidth: 160}} select label={_(msg`Chart per row`)} value={colSize}
                           onChange={e => setColSize(e.target.value)}>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                </TextField>
            </Box>

            <Grid container sx={{
                transition: 'all 0.5s ease-in-out',
            }}>
                {charts.map((chart, index) => {
                    if (chart.type === 'map' && mapReady) {
                        console.log('chart della mappa --- ', chart)
                    }
                    if (chart.type === 'map' && !mapReady) return null;
                    return (
                        <Grid item xs={12 / colSize} key={chart.title || index} p={2}>
                            <Box sx={{
                                transition: 'all 0.5s ease-in-out',
                            }}>
                                <EChart
                                    ref={(el) => (chartRefs.current[index] = el)}
                                    areasGeoJson={areasGeoJson}
                                    chart={chart}
                                    index={index}
                                    handleCharts={handleCharts}
                                    width="100%"
                                />
                            </Box>
                        </Grid>
                    );
                })}

            </Grid>

        </Box>
    );
};

export default Dashboard;
